import { ChangeEvent, MouseEvent, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useForm } from "hooks";
import { deepSet } from "logic/deepSet";
import { sortNamesBy } from "logic/sortNames";
import { DeviceConfig, FloorMarking, FloorOverride } from 'shared/models';
import { TextField } from "ui/shared/controls";

require('./FloorOverridesField.css');

type ItemProps = {
    index: number;
    from: FloorMarking;
    to: FloorMarking;
    readOnly?: boolean;
    onChange(e: ChangeEvent<HTMLInputElement | HTMLSelectElement>): void;
    onClickDelete(e: MouseEvent<HTMLElement>): void;
};

const FloorOverrideRow = ({ index, from, to, readOnly = false, onChange, onClickDelete }: ItemProps) => {
    const { t } = useTranslation();
    return (
        <>
            <TextField
                id={ `floor-override-${index}-from` }
                className="_from"
                name={ `properties.floorOverrides[${index}].from` }
                value={ from }
                readOnly={ readOnly }
                onChange={ onChange }
            />
            <i className="grey angle right icon"/>
            <TextField
                id={ `floor-override-${index}-to` }
                className="_to"
                name={ `properties.floorOverrides[${index}].to` }
                value={ to }
                readOnly={ readOnly }
                onChange={ onChange }
            />
            { readOnly
                ? <>&nbsp;</>
                : <button
                    className="ui tiny basic icon button"
                    title={ t('COMMAND.REMOVE') }
                    data-index={ index }
                    onClick={ onClickDelete }
                    disabled={ readOnly }
                >
                    <i className="ui red remove icon"/>
                </button>
            }
        </>
    );
};

type NewItemProps = {
    onAdd(floorOverride: FloorOverride): void;
};

const emptyFloorOverride = {
    from: '',
    to: '',
};

const NewFloorOverrideRow = ({ onAdd }: NewItemProps) => {
    const { t } = useTranslation();
    const { data, setData, handleChange } = useForm<FloorOverride>('floor-override', emptyFloorOverride, () => false);

    const handleClickAdd = () => {
        if (data.from && data.to) {
            onAdd(data);
            setData(emptyFloorOverride);
        }
    };

    return (
        <>
            <TextField
                id={ `new-floor-override-from` }
                name={ `from` }
                value={ data.from }
                placeholder={ t('LABEL.FROM') }
                onChange={ handleChange }
            />
            <i className="grey angle right icon"/>
            <TextField
                id={ `new-floor-override-to` }
                name={ `to` }
                value={ data.to }
                placeholder={ t('LABEL.TO') }
                onChange={ handleChange }
            />
            <button
                type="button"
                className="ui tiny icon button"
                title={ t('COMMAND.ADD') }
                onClick={ handleClickAdd }
            >
                <i className="ui add icon"/>
            </button>
        </>
    );
};

export type Props = {
    className?: string;
    config: DeviceConfig;
    readOnly?: boolean;
    setData(fn: (config: DeviceConfig) => DeviceConfig): void;
    onChange(e: ChangeEvent<HTMLInputElement | HTMLSelectElement>): void;
};

export const FloorOverridesField = ({ className, config, readOnly = false, setData, onChange }: Props) => {
    const { t } = useTranslation();
    const { floorOverrides } = config.properties;

    const handleClickDelete = useCallback((e: MouseEvent<HTMLElement>) => {
        const indexString = e.currentTarget.dataset['index']!;
        const index = parseInt(indexString, 10);
        setData(config => deepSet(config, 'properties.floorOverrides', config.properties.floorOverrides.filter((_, i) => i !== index)));
    }, [ setData ]);

    const handleAdd = useCallback((newFloorOverride: FloorOverride) => {
        setData(config => deepSet(config, 'properties.floorOverrides', [ ...config.properties.floorOverrides, newFloorOverride ].sort(sortNamesBy(fo => fo.from))));
    }, [ setData ]);

    return (
        <div
            id="floor-overrides-field"
            className={ `ui field ${className || ''}` }
        >
            <label>
                { t('LABEL.FLOOR_OVERRIDES') }
            </label>
            <div className="_container">
                <div className="_table">
                    { !readOnly &&
                        <NewFloorOverrideRow
                            onAdd={ handleAdd }
                        />
                    }

                    { floorOverrides.sort(sortNamesBy(fo => fo.from)).map((fm, i) =>
                        <FloorOverrideRow
                            key={ fm.from }
                            index={ i }
                            from={ fm.from }
                            to={ fm.to }
                            readOnly={ readOnly }
                            onChange={ onChange }
                            onClickDelete={ handleClickDelete }
                        />
                    ) }
                </div>
            </div>
        </div>
    );
};
